import { useApiPost, useApiPut } from '@/api/main/query'
import { PaginatedCollection } from '@/api/types'
import { Roles } from '@/entities/roles'
import { getColumns } from '@/entities/topups'
import { USER_ROLE_KEY } from '@/features/auth'
import {
  useGetDeviceData,
  useUpdateDeviceName,
  DeviceStatus,
  DeviceHeader,
  DeviceBanner,
  RenameDevice,
  ConnectNewDevice,
  deviceAPI,
} from '@/features/devices'
import { columns as messagesColumns } from '@/features/messages/table/columns'
import { MessageModel } from '@/features/messages/types/MessageTypes'
import { MobileBlockMessage } from '@/features/messages/ui/mobile/MobileBlockMessage'
import {
  useGetDisputsList,
  DisputStatus,
  useDisputModals,
  useGetDisputData,
  getDisputColumns,
  MobileBlocksOrders,
  AcceptedOrder,
  AcceptOrderDialog,
  CanceledOrder,
  CancelOrderDialog,
  OpenOrderDialog,
} from '@/features/orders'
import { FilterButtonModel } from '@/shared/components'
import { DEFAULT_TAKE } from '@/shared/constants/constants'
import { useToast } from '@/shared/hooks'
import { getStorageItem } from '@/shared/lib/storage/localStorage'
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { BaseTable, BaseTableSkeleton } from 'syncra-storybook/features'

export const Device = () => {
  const { addToast } = useToast()
  const queryClient = useQueryClient()
  const { id } = useParams<{ id: string }>()

  const userRole = getStorageItem<Roles | null>(USER_ROLE_KEY, null)

  const [modalRenameDevice, setModalRenameDevice] = useState(false)
  const [isConnectDeviceModalOpen, setIsConnectDeviceModalOpen] = useState(false)
  const [skipOrders, setSkipOrders] = useState(0)

  const filterButtons: FilterButtonModel[] = [
    { name: 'Входящие заявки', label: 'deals', index: 0 },
    { name: 'Споры', label: 'orders', index: 1 },
    { name: 'Сообщения', label: 'messages', index: 2 },
  ]

  const { deviceData, isLoading: isDeviceLoading } = useGetDeviceData({ deviceId: id ?? '' })

  const { handleUpdateName } = useUpdateDeviceName(id ?? '', () => setModalRenameDevice(false))

  const { mutate: enableDevice } = useApiPut<void, { id: string }>(
    deviceAPI.enableDevice(id ?? ''),
    {
      onSuccess: () => {
        addToast('Успех!', 'Устройство запущено.', 'success')
        queryClient.invalidateQueries({ queryKey: ['device', id] })
      },
      onError: (error) => {
        addToast('Ошибка!', error.message, 'error')
      },
    }
  )

  const { mutate: disableDevice } = useApiPut<void, { id: string }>(
    deviceAPI.disableDevice(id ?? ''),
    {
      onSuccess: () => {
        addToast('Успех!', 'Устройство остановлено.', 'success')
        queryClient.invalidateQueries({ queryKey: ['device', id] })
        queryClient.invalidateQueries({ queryKey: ['devices'], exact: false })
      },
      onError: (error) => {
        addToast('Ошибка!', error.message, 'error')
      },
    }
  )

  const handleUpdateStatus = (id: string) => {
    if (deviceData.deviceStatus === DeviceStatus.Active) {
      disableDevice({ id })
    } else if (deviceData.deviceStatus === DeviceStatus.Disabled) {
      enableDevice({ id })
    }
  }

  const handleOpenConnectModal = () => {
    if (deviceData) {
      setIsConnectDeviceModalOpen(true)
    }
  }

  const handleCloseConnectModal = () => {
    setIsConnectDeviceModalOpen(false)
    queryClient.invalidateQueries({ queryKey: ['devices'] })
  }

  const {
    mutate: getMessages,
    data: messagesData,
    error: messagesError,
    isPending: messagesIsLoad,
  } = useApiPost<
    PaginatedCollection<MessageModel[]>,
    {
      skip: number
      take: number
    }
  >(deviceAPI.getMessages(id ?? ''))

  useEffect(() => {
    getMessages({ skip: 0, take: 10 })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (messagesError) {
      addToast('Ошибка!', messagesError.message, 'error')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messagesError])

  const {
    data: queryDisputsData,
    error: disputsError,
    isLoading: isDisputsLoad,
    refetch: refetchDisputs,
  } = useGetDisputsList({
    skip: skipOrders,
    take: DEFAULT_TAKE,
    status: DisputStatus.Underfined,
  })
  const { activeDisputId, confirmedAmount, modalSetters, modalStates, handlers } =
    useDisputModals(refetchDisputs)
  const { disputData, error: disputError } = useGetDisputData({ disputId: activeDisputId })

  useEffect(() => {
    if (disputError) {
      addToast('Ошибка загрузки данных!', disputError.message, 'error')
    }
  }, [addToast, disputError])

  const currentColumns = {
    deals: getColumns({
      onConfirm: () => {},
      userRole,
    }),
    orders: getDisputColumns({
      openDisputDialog: handlers.openDisputDialog,
      inProgressDisputDialog: handlers.inProgressDisputDialog,
      userRole,
    }),
    messages: messagesColumns,
  }

  // const deviceDisputs = queryDisputsData?.items?.filter((item) => item.deviceId === id)

  return (
    <>
      <div className="flex flex-col gap-8">
        <DeviceHeader
          device={deviceData}
          isLoading={isDeviceLoading}
          onEdit={() => setModalRenameDevice(true)}
          onUpdateStatus={handleUpdateStatus}
          onReassignDevice={handleOpenConnectModal}
        />
        <DeviceBanner device={deviceData} isLoading={isDeviceLoading} />
      </div>
      <TabGroup className="mt-[40px]">
        <TabList className="flex gap-0.5 mb-[30px] md:mb-8 overflow-x-auto scrollbar-hide">
          {filterButtons.map((button) => (
            <Tab
              key={button.index}
              className="aria-disabled:bg-transparent disabled:bg-transparent dark:aria-disabled:bg-transparent dark:disabled:bg-transparent aria-disabled:opacity-75 disabled:opacity-75 px-4 py-2 border-0 rounded-lg focus-visible:outline-0 focus:outline-none data-[hover]:ring-1 data-[selected]:ring-1 data-[selected]:ring-current focus-visible:ring-2 focus-visible:ring-primary data-[hover]:ring-inset data-[selected]:ring-inset text-[#939BA2] data-[selected]:dark:text-primary-dark data-[selected]:text-primary whitespace-nowrap aria-disabled:cursor-not-allowed disabled:cursor-not-allowed"
            >
              {button.name}
            </Tab>
          ))}
        </TabList>
        <TabPanels>
          {filterButtons.map((button) => {
            const tabKey = button.label as keyof typeof currentColumns

            return (
              <TabPanel key={button.index}>
                {tabKey === 'messages' ? (
                  messagesError ? (
                    <p>Ошибка загрузки данных.</p>
                  ) : messagesIsLoad ? (
                    <BaseTableSkeleton />
                  ) : !messagesData?.items || messagesData.items.length === 0 ? (
                    <p>Сообщений нет.</p>
                  ) : (
                    <BaseTable
                      key={button.index}
                      columns={currentColumns[tabKey]}
                      data={messagesData.items}
                      mobileCards={messagesData.items.map((message) => (
                        <MobileBlockMessage message={message} />
                      ))}
                      cardClasses="border-0 flex flex-col gap-6 md:gap-10"
                    />
                  )
                ) : tabKey === 'orders' ? (
                  disputsError ? (
                    <p>Ошибка загрузки данных.</p>
                  ) : isDisputsLoad ? (
                    <BaseTableSkeleton />
                  ) : !queryDisputsData || queryDisputsData.items?.length === 0 ? (
                    <p>Споров нет.</p>
                  ) : (
                    <BaseTable
                      key={button.index}
                      columns={currentColumns[tabKey]}
                      data={[]}
                      cardClasses="border-0 flex flex-col gap-6 md:gap-10"
                      onPageChange={(newSkip) => setSkipOrders(newSkip)}
                      mobileCards={
                        <MobileBlocksOrders
                          data={[]}
                          openDisputDialog={handlers.openDisputDialog}
                          inProgressDisputDialog={handlers.inProgressDisputDialog}
                        />
                      }
                      isRowLink
                      navigateTo="orders"
                    />
                  )
                ) : (
                  <BaseTable
                    key={button.index}
                    columns={currentColumns[tabKey]}
                    data={[]}
                    cardClasses="border-0 flex flex-col gap-6 md:gap-10"
                    // mobileCards={queryTopupsData?.items?.map((deal) => (
                    //   <MobileBlockDeal key={deal.id} deal={deal} />
                    // ))}
                  />
                )}
              </TabPanel>
            )
          })}
        </TabPanels>
      </TabGroup>
      {/* Модальные окна */}

      <RenameDevice
        deviceData={deviceData}
        modalVisible={modalRenameDevice}
        setModalVisible={setModalRenameDevice}
        onSubmit={handleUpdateName}
      />

      <ConnectNewDevice
        modalVisible={isConnectDeviceModalOpen}
        setModalVisible={handleCloseConnectModal}
        device={{ key: deviceData.key, qrCode: deviceData.qrCode }}
      />

      {/* Споры */}
      <OpenOrderDialog
        disputId={disputData.id}
        isModalOpen={modalStates.isDisputActionOpen}
        setIsModalOpen={modalSetters.setIsDisputActionOpen}
        onSubmit={handlers.acceptDispute}
        onCancel={handlers.rejectDisput}
      />

      <AcceptOrderDialog
        amount={confirmedAmount}
        isModalOpen={modalStates.isConfirmAmountOpen}
        setIsModalOpen={modalSetters.setIsConfirmAmountOpen}
        onAction={handlers.confirmAmount}
      />
      <AcceptedOrder
        isModalOpen={modalStates.isAcceptResultOpen}
        onConfirm={handlers.closeAcceptResult}
      />

      <CancelOrderDialog
        isModalOpen={modalStates.isRejectConfirmOpen}
        onClose={modalSetters.setIsRejectConfirmOpen}
        onSubmit={handlers.closeRejectDialog}
      />
      <CanceledOrder
        isModalOpen={modalStates.isRejectResultOpen}
        onConfirm={handlers.closeRejectResult}
      />
    </>
  )
}
